import { ButtonGroup, Separator, Spacer, styled, theme } from 'design-system';
import Image from 'next/image';
import { useRouter } from 'next/router';
import { isUploadErrorPayload, UploadButtonNew } from 'preflight-core';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useEventTracking } from '../../contexts/tracking-context';
import { mutateString } from '../../utils/replace-language';
import { UploadState } from '../../utils/use-upload-document';
import { Voucher } from '../../vouchers/get-voucher';

export const V1Submission3StepsSection = ({
  langCheckCount,
  techCheckCount,
  voucher,
  price,
  publisher,
  selectedPaymentCurrency,
  localCurrencyEnabled,
  uploadState,
  onFileSelected,
  setOtherSectionClicked,
  setErrorSection,
  setErrorScreen,
  errorSection,
  errorScreen,
  isFileSelected,
  showUpload = true,
  customStep1 = false,
}: {
  langCheckCount: number;
  techCheckCount: number;
  voucher: Voucher | null;
  price?: string | number;
  publisher?: string;
  selectedPaymentCurrency?: string;
  localCurrencyEnabled?: boolean;
  uploadState: UploadState;
  onFileSelected: (file: File) => void;
  setOtherSectionClicked: (val: boolean) => void;
  setErrorSection: (val: string) => void;
  setErrorScreen: (val: boolean) => void;
  errorSection?: string;
  errorScreen?: boolean;
  isFileSelected?: boolean;
  showUpload?: boolean;
  customStep1?: boolean;
}) => {
  const [errorMessage, setErrorMessage] = useState('');
  const router = useRouter();

  useEffect(() => {
    if (
      uploadState.name === 'error' &&
      isUploadErrorPayload(uploadState.error?.json)
    ) {
      setErrorMessage(uploadState.error?.json?.errorReason);
      document.getElementById('error-message')?.focus();
    }
  }, [uploadState.name]);
  const { trackEvent } = useEventTracking();

  const t = useTranslation('homepage').t;
  const c = useTranslation('common').t;

  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  if (!isClient) {
    return null;
  }

  const priceText = `${price} ${
    selectedPaymentCurrency &&
    selectedPaymentCurrency === 'INR' &&
    localCurrencyEnabled
      ? ' (tax included)'
      : ''
  }`;
  const extendedString = !router.query.journalSlug
    ? `_${publisher === 'demo-publisher' ? 'endnote' : publisher}`
    : '';

  return (
    <FullWidthWrapper>
      <Container>
        <SubissionStepsContainer css={{ position: 'relative' }}>
          <Step>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              <SubmissionSteps>1</SubmissionSteps>
              <Separator
                css={{
                  minHeight: !showUpload ? 260 : errorScreen ? 340 : 290,
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '2px',
                  background: `repeating-linear-gradient(
                  to bottom,
                  rgba(5, 30, 87, 1),
                  rgba(5, 30, 87, 1) 2px,
                  transparent 2px,
                  transparent 4px
                )`,
                  display: 'none',
                  '@lg': {
                    display: 'inline-block',
                  },
                }}
                orientation="vertical"
              />
            </div>
            <StepText>
              <StepTitle>
                {customStep1
                  ? t('gjd.step1_title', 'Register and upload your manuscript')
                  : t(`section3_V1.your_manuscript${extendedString}`)}
              </StepTitle>
              <StepDescription>
                {customStep1
                  ? t(
                      'gjd.step1_description',
                      'Create your free account on Paperpal and upload your manuscript (.doc/.docx/.pdf) to evaluate it against a set of standard language and technical checks.',
                    )
                  : publisher === 'aipp'
                  ? `${t('section3_V1.your_manuscript_content.partOne')} ${
                      langCheckCount + techCheckCount
                    } ${t('section3_V1.your_manuscript_content.part_aipp')}`
                  : publisher === 'paperpal-preflight'
                  ? `${t(
                      `section3_V1.your_manuscript_content.part_to_upload_manuscript_pp`,
                    )}  `
                  : `${t(
                      `section3_V1.your_manuscript_content.part_to_upload_manuscript${extendedString}`,
                    )}  `}
              </StepDescription>
              <Spacer height={10} />
              {showUpload &&
                (errorSection === 'Section_3_V1' && errorScreen ? (
                  <LeftReadyToSubmitSection style={{ flex: '0 0 auto' }}>
                    <div
                      id="error-message"
                      tabIndex={-1}
                      style={{
                        color: theme.colors.redAlpha10.value,
                        fontWeight: 600,
                        marginBottom: '8px',
                        width: 400,
                      }}
                    >
                      {' '}
                      {errorMessage}
                    </div>
                    <SectionButtonStyling>
                      <Spacer height={10} />
                      <ButtonGroup>
                        {uploadState.name === 'error' ? (
                          <UploadButtonNew
                            id="Try-Uploading-Again"
                            disabled={isFileSelected || false}
                            onClick={() => {
                              trackEvent([
                                'Manuscript upload intent',
                                'Upload click',
                                'Try uploading file again',
                                {},
                              ]);
                            }}
                            onFileSelected={onFileSelected}
                            size="small"
                            text={t(
                              'tryagainButton.try_uploading_again',
                              'Try uploading file again',
                            )}
                          />
                        ) : (
                          <UploadButtonNew
                            id="Try-Uploading-Again"
                            disabled={true}
                            onClick={() => {}}
                            onFileSelected={onFileSelected}
                            size="small"
                            text={t(
                              'tryagainButton.try_uploading_again',
                              'Try uploading file again',
                            )}
                          />
                        )}
                      </ButtonGroup>
                    </SectionButtonStyling>
                  </LeftReadyToSubmitSection>
                ) : customStep1 ? (
                  <>
                    <Spacer height={10} />
                    <button
                      onClick={() => {
                        onFileSelected(new File([], 'redirect'));
                      }}
                      style={{
                        color: 'rgba(0, 84, 241, 1)',
                        border: `1px solid rgba(0, 84, 241, 1)`,
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(0, 84, 241, 1)',
                        fontSize: '16px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        fontWeight: '600',
                        borderRadius: '6px',
                        width: '220px',
                      }}
                    >
                      {t('gjd.register_cta', 'Register for a free check')}
                    </button>
                  </>
                ) : (
                  <>
                    <Spacer height={10} />
                    <UploadButtonNew
                      id="start-now"
                      data-test-id="start-now"
                      aria-label="start-now"
                      disabled={
                        uploadState.name === 'loading' ||
                        uploadState.name === 'success'
                      }
                      onClick={() => {
                        trackEvent([
                          'Manuscript upload intent',
                          'Upload click',
                          'Get your manuscript checked',
                          {
                            Section: 'Section_3_V1',
                          },
                        ]);
                        setOtherSectionClicked(false);
                        if (
                          errorSection !== 'Section_3_V1' &&
                          errorSection !== ''
                        ) {
                          setOtherSectionClicked(true);
                          setErrorScreen(false);
                        }
                        setErrorSection('Section_3_V1');
                      }}
                      onFileSelected={onFileSelected}
                      size="tiny"
                      isIcon={true}
                      text={t('section3_V1.start_now', 'Start now')}
                      isHover={true}
                      optionalLanguageText={{
                        processing: t('common.processing'),
                      }}
                      style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        padding: '8px',
                        textAlign: 'center',
                        borderRadius: 6,
                        color: 'rgba(0, 84, 241, 1)',
                        border: `1px solid rgba(0, 84, 241, 1)`,
                        backgroundColor: 'transparent',
                        borderColor: 'rgba(0, 84, 241, 1)',
                        fontSize: '16px',
                        boxShadow: 'none',
                        width: '180px',
                      }}

                      // isBlueBack={true}
                    />
                  </>
                ))}
            </StepText>
          </Step>
          <Step>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              <SubmissionSteps>2</SubmissionSteps>
              {/* <Separator
                css={{
                  minHeight: '420%',
                  position: 'absolute',
                  top: '100%',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '2px',
                  background: `repeating-linear-gradient(
                  to bottom,
                  rgba(5, 30, 87, 1),
                  rgba(5, 30, 87, 1) 2px,
                  transparent 2px,
                  transparent 4px
                )`,
                  display: 'none',
                  '@lg': {
                    display: 'inline-block',
                  },
                }}
                orientation="vertical"
              /> */}
            </div>
            <StepText>
              <StepTitle>
                {t(`section3_V1.free_submission_report${extendedString}`)}
              </StepTitle>
              <StepDescription>
                {publisher === 'aipp'
                  ? t('section3_V1.free_report_content_aipp')
                  : t('section3_V1.free_report_content')}
              </StepDescription>
            </StepText>
          </Step>
          <Step>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
              }}
            >
              <SubmissionSteps>3</SubmissionSteps>
            </div>
            <StepText>
              <StepTitle>
                {customStep1
                  ? t(
                      'gjd.step3_title',
                      'Purchase and download the edited file',
                    )
                  : mutateString(
                      t('section3_V1.edited_file_heading'),
                      voucher
                        ? { '{type}': c('voucher') }
                        : { '{type}': c('fee') },
                    )}
              </StepTitle>
              <StepDescription>
                {customStep1
                  ? t(
                      'gjd.step3_description',
                      'Purchase Paperpal Prime and get unlimited free AI-edited files for the duration of your subscription. AI-edited file contains edit suggestions applied in tracks and comments.',
                    )
                  : mutateString(
                      t('section3_V1.edited_file_content'),
                      voucher
                        ? { '{priceText}': c('one_voucher') }
                        : { '{priceText}': priceText },
                    )}
              </StepDescription>
            </StepText>
          </Step>
        </SubissionStepsContainer>
        <SubmissionImageSection
          style={
            !router.query.journalSlug ? { width: '464px', height: '424' } : {}
          }
          src={
            !router.query.journalSlug
              ? '/v1-landing-page/submission-3-steps-endnote.svg'
              : '/v1-landing-page/submission-3-steps.svg'
          }
          alt="Submission-readiness-image"
          width={80}
          height={100}
          size={{ '@md': 'medium' }}
        />
      </Container>
      {/* <Spacer height={10} /> */}
    </FullWidthWrapper>
  );
};

V1Submission3StepsSection.displayName = 'V1Submission3StepsSection';

const FullWidthWrapper = styled('div', {
  width: '100%',
});

const Container = styled('div', {
  display: 'flex',
  justifyContent: 'space-between',
});

const SubissionStepsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  paddingRight: '70px',
  gap: 14,
});

const Step = styled('div', {
  display: 'flex',
  alignItems: 'flex-start',
  marginBottom: '20px',
  gap: 20,
});

const StepText = styled('div', {
  flex: 1,
});

const StepTitle = styled('h3', {
  margin: 0,
  fontSize: '22px',
  fontWeight: 'bold',
  width: '100%',
  color: 'rgba(5, 30, 87, 1)',
});

const StepDescription = styled('p', {
  margin: '10px 0 0 0',
  fontSize: '16px',
  color: 'rgba(5, 30, 87, 1)',
  fontWeight: 400,

  width: 410,
  lineHeight: '1.4',
});

const SubmissionSteps = styled('div', {
  width: 32,
  height: 32,
  fontSize: 16,
  color: 'rgba(5, 30, 87, 1)',
  backgroundColor: 'rgba(221, 233, 255, 1)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: '50%',
  fontWeight: 500,
  textTransform: 'capitalize',
});

const SubmissionImageSection = styled(Image, {
  width: '480px',
  height: '490px',
  paddingBottom: '50px',
  marginRight: 50,
  variants: {
    size: {
      small: {
        paddingBottom: '0px',
        marginTop: '110px',
        width: '25%',
        paddingTop: '20px',
        marginLeft: '10px',
      },
      medium: {
        width: '35%',
        padding: '0px',
        background: 'none',
      },
    },
  },
});

const SectionButtonStyling = styled('div', {
  '>button': {
    minWidth: '348px',
    height: '63px',
  },
  '& > button:hover': {
    backgroundColor: theme.colors.blue11,
  },
});

const LeftReadyToSubmitSection = styled('div', {
  flex: 1,
});
